import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStore } from '@xyflow/react'
import { Plus } from 'lucide-react'
import type { MovingRect } from './utils/canvasAlignment'
import { SPAWN_SIDES, getNearSpawnSide, type SpawnSide } from './utils/canvasSpawn'
import './CanvasSpawnHandles.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
 * Wie lange der Zeiger auf einem Punkt STEHEN muss, bis die Vorschau kommt.
 *
 * Die Vorschau rückt Zettel (**Dazwischen anlegen**) — ohne Wartezeit zuckte
 * eine halbe Reihe schon, wenn man den Zeiger nur über den Canvas führt und
 * dabei einen Punkt streift. Gewartet wird auf die Absicht, nicht auf den Weg.
 *
 * Der KLICK wartet nicht, und das **Plus** wartet auch nicht: Die Nähe ist
 * eine Auskunft über den Zeiger und darf sofort kommen. Gewartet wird allein
 * auf das, was fremde Zettel bewegt.
 *
 * **Kurz gehalten.** Ein Zeiger, der über den Canvas fährt, ist in weit unter
 * 100ms über den Punkt hinweg — mehr als das braucht es nicht, und alles
 * darüber liest sich nicht als Absicht, sondern als Trägheit.
 */
export const SPAWN_HOVER_DELAY = 140

export interface CanvasSpawnHandlesProps {
  /** Lage und Maße des Zettels in Canvas-Koordinaten. */
  rect: MovingRect
  /** Der Zeiger steht auf einem Punkt — oder auf keinem mehr (`null`). */
  onHover: (side: SpawnSide | null) => void
  /** Klick auf einen Punkt. */
  onSpawn: (side: SpawnSide) => void
  /**
   * Solange eine Geste nachklingt (die Nachlaufzeit aus
   * `useCanvasPointerBusy`), verschluckt der Punkt ein Betreten. Nach dem
   * Loslassen stehen die Punkte plötzlich unter dem ruhenden Zeiger, und der
   * Browser meldet ein `pointerenter` ohne jede Bewegung. Danach läuft
   * nichts von selbst an: Erst wer den Punkt verlässt und erneut anfährt,
   * meint ihn.
   *
   * **Der zweite Riegel steht im Rumpf** und misst die Lage statt der Zeit
   * (siehe dort): Diese Frist deckt nur das Betreten ab, das SOFORT kommt.
   */
  suspended?: boolean
}

/**
 * Die vier **Anlegepunkte** eines ausgewählten Zettels — mittig an jeder
 * Kante, ein Stück außerhalb (wie in Miro und FigJam). Ein Klick legt in
 * dieser Richtung einen leeren Zettel an. Sobald der Zeiger einer Kante NAH
 * kommt, hebt sich ihr Punkt (Plus); steht er dann AUF dem Punkt, zeigt der
 * Canvas dort — nach einer kurzen Ruhe — die Vorschau
 * (`CanvasSpawnPreview`). Drei Stufen, bewusst: Die Nähe macht den Punkt
 * auffindbar, der Treffer meint ihn, das Verweilen meint es ernst. Die
 * Vorschau bleibt so eine Antwort auf die Absicht — sonst zuckte bei jedem
 * Vorbeiziehen eine halbe Reihe.
 *
 * Entwurf: Figma „Note Selected" `4873:110643` (Zustände), `4873:110556`
 * (Lage: 12px Abstand zur Kante), `4902:193020` (gehoben, bei Nähe).
 *
 * **Nähe statt Hover:** Der Punkt ist in Ruhe 8px klein — wer ihn erst
 * treffen müsste, um ihn zu heben, fände ihn kaum. Deshalb horcht die
 * Komponente auf den Zeiger über dem ganzen Canvas und rechnet, welcher
 * Kante er nah ist (`getNearSpawnSide`); der Punkt dieser Kante steht dann
 * gehoben da, mit Plus, und lässt sich treffen. Ein Fenster-Listener, keine
 * unsichtbare Fläche: Eine Fläche über dem Zettel nähme ihm Klick und Zug.
 *
 * **Im `ViewportPortal` gezeichnet, nicht im Node** — aus demselben Grund wie
 * die Ziehpunkte (`.canvas-note-handle`): `.react-flow__node` ist ein eigener
 * Stapelkontext, nichts darin käme über die Hilfslinie (2000). Anders als die
 * Ziehpunkte sind diese Punkte Ansicht UND Trefferfläche in einem.
 *
 * `nodrag`/`nopan`: React Flow soll den Klick weder als Zug noch als
 * Verschieben der Fläche lesen.
 *
 * **Die Vorschau kommt erst nach einer kurzen Ruhe** auf dem Punkt
 * (`SPAWN_HOVER_DELAY`) — sie ist der dritte und teuerste Schritt nach Nähe
 * und Treffer, denn sie bewegt fremde Zettel.
 *
 * **Solange der Zeiger auf einem Punkt steht, bleiben die Punkte, wo sie
 * sind.** Am oberen und linken Punkt rückt der ausgewählte Zettel in der
 * Vorschau selbst mit (**Dazwischen anlegen**) — wanderten seine Punkte mit
 * ihm, verlöre der Zeiger den Punkt unter sich, die Vorschau nähme sich
 * zurück, der Zettel käme wieder, der Zeiger stünde wieder auf dem Punkt, und
 * das mehrmals pro Sekunde. Eingefroren wird deshalb die Lage, an der die
 * Vorschau begann. Dieselbe Begründung wie beim eingefrorenen Ausgangsbild des
 * **Platz tauschens**.
 */
export const CanvasSpawnHandles: React.FC<CanvasSpawnHandlesProps> = ({ rect, onHover, onSpawn, suspended = false }) => {
  const { t } = useTranslation()
  const transform = useStore(state => state.transform)
  const domNode = useStore(state => state.domNode)
  const [near, setNear] = useState<SpawnSide | null>(null)
  /* Wo der Zeiger zuletzt stand, in Canvas-Koordinaten — für den Riegel unten. */
  const pointer = useRef<{ x: number; y: number } | null>(null)
  /*
    **Der Zettel ist unter den ruhenden Zeiger gerückt.** Dann schweigen seine
    Punkte, bis der Zeiger ihn einmal verlassen hat.

    Warum das nötig ist, obwohl es `suspended` schon gibt: Nach dem
    **Dazwischen schieben** nimmt der abgesetzte Zettel die führende Kante des
    Bezugs ein, und das getroffene Band liegt unmittelbar davor — der Zeiger
    steht also regelmäßig genau auf dem Anlegepunkt dieser Kante. `suspended`
    verschluckt dieses Betreten nur, wenn es innerhalb der Nachlaufzeit kommt;
    ein Element aber, das unter einen RUHENDEN Zeiger rückt, meldet sein
    `pointerenter` oft erst bei der nächsten Bewegung der Hand — da ist jede
    Frist längst abgelaufen. Deshalb hier kein Wecker, sondern die Lage: Wer
    den Zettel verlässt (`getNearSpawnSide` liefert `null`), löst den Riegel.
  */
  const muted = useRef(false)
  const [frozen, setFrozen] = useState<MovingRect | null>(null)
  const shown = frozen ?? rect

  // Immer die aktuellen Werte im Listener, ohne ihn bei jedem Frame neu zu
  // hängen: Beim Zoomen und Schieben wechselt `transform` laufend.
  const latest = useRef({ rect: shown, raw: rect, transform, domNode })
  latest.current = { rect: shown, raw: rect, transform, domNode }

  /* Der Wecker für die Wartezeit, und ob aus ihm schon eine Vorschau wurde. */
  const waiting = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previewing = useRef(false)
  const stopWaiting = () => {
    if (waiting.current === null) return
    clearTimeout(waiting.current)
    waiting.current = null
  }
  // Ein laufender Wecker darf nicht in eine abgeräumte Auswahl feuern.
  useEffect(() => stopWaiting, [])

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const { rect, transform, domNode } = latest.current
      const [tx, ty, zoom] = transform
      const bounds = domNode?.getBoundingClientRect()
      const point = {
        x: (event.clientX - (bounds?.left ?? 0) - tx) / zoom,
        y: (event.clientY - (bounds?.top ?? 0) - ty) / zoom,
      }
      pointer.current = point
      const side = getNearSpawnSide(rect, point, zoom)
      // Der Zettel ist verlassen — der Riegel fällt.
      if (side === null) muted.current = false
      setNear(side)
    }
    window.addEventListener('pointermove', onMove)
    return () => window.removeEventListener('pointermove', onMove)
  }, [])

  /*
    Der Riegel wird gesetzt, wenn der Zettel RÜCKT und der Zeiger ihm dabei
    nah ist. Die eigene Vorschau rückt ihn auch (**Dazwischen anlegen**, oben
    und links) — die zählt nicht, sonst verstellte sich der Punkt nach dem
    ersten Überfahren selbst. Erkennbar ist sie an `previewing`/`frozen`.
  */
  const place = useRef(rect)
  useEffect(() => {
    const previous = place.current
    place.current = rect
    if (previewing.current || frozen) return
    const still = previous.x === rect.x && previous.y === rect.y
      && previous.width === rect.width && previous.height === rect.height
    if (still) return
    const point = pointer.current
    if (point && getNearSpawnSide(rect, point, latest.current.transform[2]) !== null) muted.current = true
  }, [rect, frozen])

  return (
    <>
      {SPAWN_SIDES.map(side => (
        <WithTooltip key={side} content={t(`projectCanvas.note.spawn.${side}`)}>
          <button
            type="button"
            className={`canvas-spawn-handle canvas-spawn-handle--${side}${near === side ? ' is-near' : ''} nodrag nopan`}
            aria-label={t(`projectCanvas.note.spawn.${side}`)}
            style={{
              left: side === 'left' ? shown.x : side === 'right' ? shown.x + shown.width : shown.x + shown.width / 2,
              top: side === 'top' ? shown.y : side === 'bottom' ? shown.y + shown.height : shown.y + shown.height / 2,
              // Gegen den Zoom, wie die Ziehpunkte: Der Punkt bleibt bildschirmgroß.
              scale: 'var(--canvas-scale, 1)',
            }}
            onPointerEnter={() => {
              // Ein Betreten ohne Bewegung zählt nicht — weder frisch nach einer
              // Geste (`suspended`) noch, wenn der Zettel unter den Zeiger
              // gerückt ist (`muted`).
              if (suspended || muted.current) return
              stopWaiting()
              waiting.current = setTimeout(() => {
                waiting.current = null
                previewing.current = true
                // Die Lage von JETZT, nicht die beim Betreten: Bis der Wecker
                // klingelt, kann der Zettel selbst gerückt sein.
                setFrozen(latest.current.raw)
                onHover(side)
              }, SPAWN_HOVER_DELAY)
            }}
            onPointerLeave={() => {
              stopWaiting()
              // Kam es nie zu einer Vorschau, gibt es auch nichts zurückzunehmen
              // — ein Streifen des Punkts soll den Canvas gar nicht erst erreichen.
              if (!previewing.current) return
              previewing.current = false
              setFrozen(null)
              onHover(null)
            }}
            // Der Zettel darunter darf den Klick nicht sehen — er würde sonst
            // die Auswahl wechseln oder den Bearbeiten-Modus öffnen.
            onPointerDown={event => event.stopPropagation()}
            onClick={event => {
              event.stopPropagation()
              stopWaiting()
              previewing.current = false
              setFrozen(null)
              onSpawn(side)
            }}
          >
            <Plus size={16} strokeWidth={3} aria-hidden="true" />
          </button>
        </WithTooltip>
      ))}
    </>
  )
}
